import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { getOrder, updateOrder } from '@app/adapter/order-service';
import { organizationSelector } from '@app/domain/organization';
import { OrderCancelInput, OrderCancelForm } from '@app/schemas/order';
import {
  NOT_HIRED_REASON_OTHER,
  notHiredReasons,
  Order,
  OrderCancelType,
  OrderLocalized,
  OrderStatus,
} from '@app/types/order';
import { isAgencyOrg } from '@app/utils/organization';
import { useSetSnackbar } from '@app/utils/useSetSnackbar';

interface CancelDialogProps {
  onClose: () => void;
  onSubmit?: () => void;
  open: boolean;
  orders?: Order[] | OrderLocalized[];
}

export function CancelDialog({
  onClose,
  onSubmit,
  open,
  orders,
}: CancelDialogProps): ReactElement {
  const setSnackbar = useSetSnackbar();
  const organizationState = useRecoilValue(organizationSelector);
  const [isLoading, setIsLoading] = useState(false);
  const userName =
    orders?.length === 1
      ? `${orders[0].customer?.user?.customFields?.familyName} ${orders[0].customer?.user?.customFields?.firstName}さん`
      : `チェックした${orders?.length}名`;
  const isAgency = isAgencyOrg(organizationState);
  // NOTE:採用キャンセルはメール送信の要否を選べないため、非採用のときのみチェックボックスを表示する
  const isNotHired = (
    orders as Array<Order | OrderLocalized> | undefined
  )?.every((order) => order.status === OrderStatus.PENDING);
  /**
   * NOTE:人材紹介企業は自社から直接連絡するためメール送信の要否を選べる。
   * 医療機関は従来どおり常に送信するのでチェックボックスを表示しない
   */
  const canChooseMail = isAgency && isNotHired;
  const { control, formState, watch, handleSubmit, reset } =
    useForm<OrderCancelInput>(OrderCancelForm);

  const handleClose = useCallback((): void => {
    onClose();
    reset();
  }, [onClose, reset]);

  const updateOrders = async (data: OrderCancelInput) => {
    if (!orders?.length) return;
    setIsLoading(true);

    if (data.cancelReason === NOT_HIRED_REASON_OTHER) {
      data.cancelReason = data.otherReason || '';
    }
    try {
      const results = await Promise.all(
        orders.map(async (order) => {
          const latestOrder = await getOrder(order.organization, order.id);
          if (latestOrder.data.status !== order.status) {
            // NOTE:一覧の読み込み後に状態が変わった応募は更新しない
            return false;
          }
          const isNotHiredOrder = order.status === OrderStatus.PENDING;
          await updateOrder(order.organization, order.id, {
            canceledAt: new Date().toISOString(),
            customFields: {
              ...latestOrder.data.customFields,
              cancelMessage: data.cancelReason,
              cancelType: isNotHiredOrder
                ? OrderCancelType.SUPPLY_STATUS_NOT_ACCEPTED
                : OrderCancelType.SUPPLY_STATUS_ACCEPTED,
              // NOTE:チェックボックスを出さないケースは未設定にして常に送信させる
              ...(isAgency &&
                isNotHiredOrder && {
                  sendNotHiredMailFlag: data.sendNotHiredMail,
                }),
            },
            status: OrderStatus.CANCELED,
          });
          return true;
        })
      );
      const updatedCount = results.filter((updated) => updated).length;
      if (!updatedCount) {
        setSnackbar(
          true,
          '応募の状態が変わったため更新できませんでした',
          'error'
        );
        return;
      }
      // NOTE:状態が変わって更新できなかった応募があるため、実際に更新した件数を伝える
      setSnackbar(
        true,
        updatedCount === orders.length
          ? `${userName}を不採用にしました。`
          : `${updatedCount}名を不採用にしました。`,
        'success'
      );
      onSubmit && onSubmit();
    } catch (e) {
      console.error(e);
      setSnackbar(true, '不採用の登録に失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
      setIsLoading(false);
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="h6">{`${userName}を非採用にしますか？`}</Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* NOTE:Selectに幅を取られてラベルが折り返さないよう縮小させない */}
              <Typography variant="body2" sx={{ flexShrink: 0 }}>
                非採用理由
                <Typography component="span" color="error">
                  *
                </Typography>
              </Typography>
              <Controller
                name="cancelReason"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      error={!!error}
                      size="small"
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        選択してください
                      </MenuItem>
                      {notHiredReasons.map((reason, index) => (
                        <MenuItem key={index} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>
            {canChooseMail && (
              <Controller
                name="sendNotHiredMail"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox {...field} checked={field.value} size="small" />
                    }
                    label={
                      <Typography variant="body2">
                        本応募案件について、非採用の旨のメールを送る
                      </Typography>
                    }
                    // NOTE:既定の負のマージンを打ち消して非採用理由のラベルと左端を揃える
                    sx={{ ml: 0 }}
                  />
                )}
              />
            )}
            <Typography variant="caption">
              ※不採用理由は本人には通知されません
            </Typography>
          </Stack>
          {watch('cancelReason') === NOT_HIRED_REASON_OTHER && (
            <Stack>
              <Typography variant="body2">補足コメント</Typography>
              <Controller
                name="otherReason"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    margin="dense"
                    placeholder="コメントを入力してください"
                    rows={3}
                    multiline
                    fullWidth
                  />
                )}
              />
            </Stack>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              color="secondary"
              variant="outlined"
              size="small"
              onClick={handleClose}
              sx={{ minWidth: '120px' }}
            >
              キャンセル
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              loading={isLoading}
              disabled={!formState.isValid}
              onClick={handleSubmit(updateOrders)}
              sx={{ minWidth: '120px' }}
            >
              不採用にする
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
